import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import moment from 'moment'
import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import EditIcon from '@mui/icons-material/Edit'
import PhoneIcon from '@mui/icons-material/Phone'
import API from '../../../services/API'
import { UserContext } from '../../../App'
import Loading from '../../loading/loadingPage'
import BigModal from '../../../components/Modals/BigModal'
import {
  SectionHeader,
  InfoList,
} from '../../fleetEquipment/vehicles/VehicleComponents/SectionKit'
import BaseLocationForm from '../BaseLocationForm'
import BaseLocationsMap from '../BaseLocationsMap'
import CoshhSection from './CoshhSection'
import DeleteBaseLocationDialog from './DeleteBaseLocationDialog'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('')

const HeroTile = ({ label, value, caption }) => (
  <Card variant="outlined" sx={{ p: 1.5 }}>
    <Typography variant="body2" color="text.secondary" noWrap>
      {label}
    </Typography>
    <Typography
      component="div"
      sx={{ fontSize: '1.1rem', fontWeight: 800, lineHeight: 1.3 }}
      noWrap
    >
      {value ?? '—'}
    </Typography>
    {caption && (
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        noWrap
      >
        {caption}
      </Typography>
    )}
  </Card>
)

const TabLabel = ({ label, count, color }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <span>{label}</span>
    {count !== undefined && (
      <Chip
        size="small"
        label={count}
        color={color}
        sx={{ borderRadius: 1, height: 18, fontSize: '0.7rem' }}
      />
    )}
  </Stack>
)

const BaseLocationPage = () => {
  const { id } = useParams()
  const { search } = useLocation()
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const { setPageTitle } = useDocumentTitle()
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [data, setData] = useState(null)
  const [coshhCount, setCoshhCount] = useState(null)
  const [openEdit, setOpenEdit] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [tab, setTab] = useState(
    new URLSearchParams(search).get('opentab') || 'overview',
  )

  const teams = user?.employee?.teams || []
  const isSiteAdmin = !!user?.security?.isSiteAdmin
  const canManageCoshh =
    isSiteAdmin ||
    ['Governance', 'Manager', 'Fleet'].some((team) => teams.includes(team))

  const loadOverview = () => {
    setLoading(true)
    setFetchError(false)
    API.get(`/base-locations/${id}/overview`)
      .then((res) => {
        if (res.status === 200) {
          setData(res.data)
          setCoshhCount(res.data.counts?.coshhSheets ?? 0)
        } else {
          setFetchError(true)
        }
      })
      .catch((e) => {
        console.error(e)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadOverview()
  }, [id])

  const base = data?.base
  const counts = data?.counts || {}

  useEffect(() => {
    if (base?.name) {
      setPageTitle(base.name)
    }
  }, [base?.name, setPageTitle])

  const tabs = useMemo(
    () => [
      { key: 'overview', label: 'Overview' },
      { key: 'vehicles', label: 'Vehicles', count: counts.vehicles ?? 0 },
      { key: 'staff', label: 'Staff', count: counts.staff ?? 0 },
      {
        key: 'coshh',
        label: 'COSHH',
        count: coshhCount ?? counts.coshhSheets ?? 0,
        color:
          (coshhCount ?? counts.coshhSheets ?? 0) > 0 ? 'success' : 'default',
      },
      { key: 'map', label: 'Map' },
    ],
    [counts, coshhCount],
  )

  if (loading) return <Loading />
  if (fetchError || !base)
    return (
      <Alert severity="error" variant="filled">
        Unable to load this base location. Please try again or contact AmbuNet
        support.
      </Alert>
    )

  const addressLine =
    [
      base.address?.lineOne,
      base.address?.lineTwo,
      base.address?.town,
      base.address?.county,
      base.address?.postCode,
    ]
      .filter(Boolean)
      .join(', ') || 'No address recorded'

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'anBlue.main',
                fontWeight: 700,
                fontSize: '1.5rem',
              }}
            >
              {getInitials(base.name)}
            </Avatar>
            <Box minWidth={0} flex={1}>
              <Typography variant="title2">{base.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {addressLine}
              </Typography>
              {base.onCallNumber && (
                <Chip
                  size="small"
                  icon={<PhoneIcon />}
                  label={`On call: ${base.onCallNumber}`}
                  component="a"
                  href={`tel:${base.onCallNumber}`}
                  clickable
                  variant="outlined"
                  sx={{ borderRadius: 1, maxWidth: 260, mt: 0.5 }}
                />
              )}
            </Box>
            {isSiteAdmin && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setOpenEdit(true)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteForeverIcon />}
                  onClick={() => setOpenDelete(true)}
                >
                  Delete
                </Button>
              </Stack>
            )}
          </Stack>

          <Box
            display="grid"
            gridTemplateColumns={{
              xs: '1fr',
              sm: '1fr 1fr',
              lg: 'repeat(4, 1fr)',
            }}
            gap={1.5}
          >
            <HeroTile label="Vehicles" value={counts.vehicles ?? 0} />
            <HeroTile
              label="Staff"
              value={counts.staff ?? 0}
              caption="With access to this base"
            />
            <HeroTile label="Active rosters" value={counts.rosters ?? 0} />
            <HeroTile
              label="Upcoming events"
              value={counts.upcomingEvents ?? 0}
            />
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{
            px: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          }}
        >
          {tabs.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              label={
                <TabLabel label={t.label} count={t.count} color={t.color} />
              }
            />
          ))}
        </Tabs>
        <Box p={2}>
          {tab === 'overview' && (
            <Stack spacing={3}>
              <Stack spacing={2}>
                <SectionHeader
                  title="Overview"
                  caption="Key details for this base"
                  action={
                    isSiteAdmin && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setOpenEdit(true)}
                      >
                        Edit details
                      </Button>
                    )
                  }
                />
                <InfoList
                  rows={[
                    { label: 'Address', value: base.address?.lineOne },
                    { label: 'Town', value: base.address?.town },
                    { label: 'County', value: base.address?.county },
                    { label: 'Postcode', value: base.address?.postCode },
                    { label: 'On-call number', value: base.onCallNumber },
                    {
                      label: 'Map point',
                      value: base.location?.coordinates ? (
                        <Chip
                          size="small"
                          label="Set"
                          color="success"
                          sx={{ borderRadius: 1, fontWeight: 700 }}
                          onClick={() => setTab('map')}
                        />
                      ) : (
                        <Chip
                          size="small"
                          label="Not set — needed for base-to-base PTS mileage"
                          color="warning"
                          sx={{ borderRadius: 1, fontWeight: 700 }}
                          onClick={() => setTab('map')}
                        />
                      ),
                    },
                    {
                      label: 'Client organisations',
                      value: `${counts.clients ?? 0}`,
                    },
                  ]}
                />
              </Stack>

              <Stack spacing={2}>
                <SectionHeader
                  title="Upcoming Events"
                  caption="Next events running from this base"
                />
                {data.upcomingEvents?.length ? (
                  <Stack divider={<Divider sx={{ opacity: 0.6 }} />}>
                    {data.upcomingEvents.map((event) => (
                      <Stack
                        key={event._id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                        py={0.9}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          component={RouterLink}
                          to={`/events/${event._id}`}
                          sx={{ color: 'inherit' }}
                        >
                          {event.title || 'Untitled event'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {event.startDate
                            ? moment(event.startDate).format('Do MMM YYYY')
                            : '—'}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No upcoming events at this base.
                  </Typography>
                )}
              </Stack>

              {data.clients?.length > 0 && (
                <Stack spacing={2}>
                  <SectionHeader
                    title="Client Organisations"
                    caption="Clients served from this base"
                  />
                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    {data.clients.map((client) => (
                      <Chip
                        key={client._id}
                        size="small"
                        label={client.orgName}
                        variant="outlined"
                        sx={{ borderRadius: 1 }}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}

          {tab === 'vehicles' && (
            <Stack spacing={2}>
              <SectionHeader
                title="Vehicles"
                caption="Fleet assigned to this base"
              />
              {data.vehicles?.length ? (
                <Stack divider={<Divider sx={{ opacity: 0.6 }} />}>
                  {data.vehicles.map((vehicle) => (
                    <Stack
                      key={vehicle._id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                      py={1}
                    >
                      <Box minWidth={0} gap={2}>
                        <Typography
                          fontWeight={700}
                          component={RouterLink}
                          to={`/fleet/${vehicle._id}`}
                          sx={{ color: 'inherit', mr: 1 }}
                        >
                          {vehicle.callSign || vehicle.regPlate}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[vehicle.regPlate, vehicle.vehicleType]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={vehicle.status || '—'}
                        color={
                          vehicle.status === 'In Service'
                            ? 'success'
                            : 'default'
                        }
                        sx={{ borderRadius: 1, fontWeight: 700 }}
                      />
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No vehicles are assigned to this base. Set a vehicle's base
                  location from its Fleet page.
                </Typography>
              )}
            </Stack>
          )}

          {tab === 'staff' && (
            <Stack spacing={2}>
              <SectionHeader
                title="Staff"
                caption="Staff whose accounts can access this base"
              />
              {data.staff?.length ? (
                <Stack divider={<Divider sx={{ opacity: 0.6 }} />}>
                  {data.staff.map((member) => (
                    <Stack
                      key={member._id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                      py={1}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {[member.firstName, member.lastName]
                          .filter(Boolean)
                          .join(' ') || member.email}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ overflowWrap: 'anywhere' }}
                      >
                        {member.email}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No staff accounts have access to this base yet. Base access is
                  set per user in Personnel.
                </Typography>
              )}
            </Stack>
          )}

          {tab === 'coshh' && (
            <CoshhSection
              base={base}
              canManage={canManageCoshh}
              onCountChange={setCoshhCount}
            />
          )}

          {tab === 'map' && (
            <Stack spacing={2}>
              <SectionHeader
                title="Map Point"
                caption="Where this base sits on the map — used for base-to-base PTS mileage and routing"
              />
              <BaseLocationsMap
                locations={[base]}
                canEdit={isSiteAdmin}
                onSaved={loadOverview}
              />
            </Stack>
          )}
        </Box>
      </Paper>

      <BigModal open={openEdit} autoWidth onClose={() => setOpenEdit(false)}>
        <BaseLocationForm
          existing={base}
          onClose={() => setOpenEdit(false)}
          onSaved={() => {
            setOpenEdit(false)
            loadOverview()
          }}
        />
      </BigModal>

      <DeleteBaseLocationDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        base={base}
        onDeleted={() => navigate('/base-locations')}
      />
    </Stack>
  )
}

export default BaseLocationPage
